import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { Subscription, merge } from 'rxjs';

import { Constants } from './constants/manage-survey-tabs.constants';

import { AuthService } from '../shared/services/auth/auth.service';

import { AuthState } from '../shared/interfaces/auth.interface';
import { Survey } from '../shared/interfaces/survey.interface';
import { EventEmitterService } from '../shared/services/event-emitter/event-emitter.service';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { BuyerApiService } from '../shared/services/buyer-api/survey.service';
import { SurveyPayloadService } from '../create-surveys/survey-payload.service';

@Component({
  selector: 'ps-ui-manage-survey-tabs',
  templateUrl: './manage-survey-tabs.component.html',
  styleUrls: ['./manage-survey-tabs.component.css'],
})
export class ManageSurveyTabsComponent implements OnChanges, OnInit, OnDestroy {
  public tabs: Tab[] = Constants.MANAGE_SURVEY_TABS;
  public activeTabIndex: number = 0;
  public activeTab: Tab = this.tabs[this.activeTabIndex];
  public enableTrafficChannels: boolean = false;
  private _userType!: string;
  @Input() survey: Survey = {} as Survey;
  @Input() set selectedSvpChildSurveyId(id: number) {
    this._selectedSvpChildSurveyIdCommand$.next(id);
  }
  @Input() set auth(data: AuthState) {
    this._auth.setState(data);
    this.enableTrafficChannels =
      !!this._auth?.buyerConfig?.enableTrafficChannels;
    this.isEnableHealthMetrics =
      this._auth.buyerConfig?.enableHealthMetrics ?? false;
  }
  @Input() isCpiEditModeDisabled: boolean = false;
  @Input() targetedActiveTabKey: string | null;
  public isServiceBuyerLoggedIn: boolean = false;
  public isEnableHealthMetrics: boolean = false;
  public readonly Constants = Constants;

  /** For a non MC-QB survey, dynamic and static tabs are one and the same thing
   *  For a MC-QB survey, dynamic tab has details of the child survey, which are dependent on locale
   *  and static tab has same details across all the locales of the MC-QB survey
   */
  public dynamicTabSurvey!: Survey;
  public staticTabSurvey!: Survey;
  public selectedSvpChildSurvey!: Survey;

  private _subscription: Subscription = new Subscription();
  private _surveyCommand$ = new EventEmitter<Survey>();
  private _selectedSvpChildSurveyIdCommand$ = new EventEmitter<number>();

  constructor(
    private _auth: AuthService,
    private _buyerApiService: BuyerApiService,
    private _surveyPayloadService: SurveyPayloadService,
    private _eventEmitterService: EventEmitterService
  ) {
    this._handleSvpChildSelectionChanges();
  }

  ngOnInit(): void {
    const authServiceSubscription =
      this._auth.loggedInAsServiceBuyer$.subscribe((isLoggedIn) => {
        if (isLoggedIn) {
          this.isServiceBuyerLoggedIn = true;
        }
      });
    this._subscription.add(authServiceSubscription);
  }

  ngOnChanges(changes: SimpleChanges = {}): void {
    this._userType = this._auth.userType;
    if (this._userType === 'supplier') {
      this.tabs = Constants.MANAGE_SURVEY_TABS.filter((tab) =>
        Constants.SURVEY_SUPPLIER_TAB_KEY.includes(tab.key)
      );
    } else {
      const filteredTabs = this._manageTab();
      this.tabs = this._mapSpecialThemeTabs(filteredTabs);
    }

    this._decideActiveTab(changes);

    if (changes.survey?.currentValue) {
      this._surveyCommand$.next(changes.survey.currentValue);
    }
  }

  private _decideActiveTab(changes: SimpleChanges): void {
    // If there are no changes in the survey, it should not reset the active tab
    if (!changes.survey?.currentValue) {
      return;
    }

    const activeIndex = this.tabs.findIndex(
      (tab) => tab.key === this.targetedActiveTabKey
    );
    const activeTabIndex = activeIndex !== -1 ? activeIndex : 0;
    if (!this._isChangeSurveyStatus(changes)) {
      this.setActiveTab(activeTabIndex);
    } else {
      this._emitChangeSurveyStatus(changes);
    }
  }

  private _manageTab() {
    const _tabsToRemove: string[] = [];
    if (this._enableMultiCountry()) {
      _tabsToRemove.push('MULTI_COUNTRY');
    }
    if (!this._enableTC()) {
      _tabsToRemove.push('TRAFFIC_CHANNELS');
    }
    if (!this._showReconTab()) {
      _tabsToRemove.push('RECONCILE');
    }
    if (!this._showMarginMaxTab()) {
      _tabsToRemove.push('MARGIN_MAX');
    }
    if (!this._showHealthChecksTab()) {
      _tabsToRemove.push('HEALTH_CHECKS');
    }
    return Constants.MANAGE_SURVEY_TABS.filter(
      (tab) => !_tabsToRemove.includes(tab.key)
    );
  }

  private _mapSpecialThemeTabs(tabs: Tab[]): Tab[] {
    if (!(this.survey.mc?.SVP && this.survey.mc?.parent)) {
      return tabs;
    }

    return tabs.map((tab) => {
      return {
        ...tab,
        ...(Constants.MC_QB.DYANAMIC_TABS.includes(tab.key) && {
          hasSpecialTheme: true,
          title: `${Constants.MC_QB.DYNAMIC_TAB_SPECIAL_SYMBOL} ${tab.title}`,
        }),
      };
    });
  }

  private _enableMultiCountry() {
    return (
      this._userType === 'buyer' &&
      !(
        this.survey.mc_unique_id &&
        (this._auth.buyerConfig?.enableMultiCountry ||
          this._auth.buyerConfig?.enableMultiCountrySingleProjectView)
      )
    );
  }

  private _enableTC() {
    return (
      this.survey.incl_excl !== 1 &&
      !this.survey.channelType &&
      ((this.enableTrafficChannels && this._userType === 'buyer') ||
        this._userType === 'operator')
    );
  }

  private _showReconTab() {
    if (this._userType === 'buyer') {
      return this._auth.buyerConfig?.reconciliations;
    }
    if (this._userType === 'operator') {
      return true;
    }
    return false;
  }

  private _showMarginMaxTab() {
    if (this._userType === 'operator' && this.survey.is_enable_margin_max) {
      return true;
    }
    return false;
  }

  private _showHealthChecksTab() {
    return this._userType === 'operator' || this.isEnableHealthMetrics;
  }

  setActiveTab(activeTabIndex: number) {
    this.activeTabIndex = activeTabIndex;
    this.activeTab = this.tabs[activeTabIndex];
  }

  private _isChangeSurveyStatus(changes: SimpleChanges) {
    return (
      !changes.survey?.firstChange &&
      changes.survey?.currentValue?.status !==
        changes.survey?.previousValue?.status
    );
  }

  private _emitChangeSurveyStatus(changes: SimpleChanges) {
    this._eventEmitterService.changeSurveyStatus({
      surveyId: this.survey.id,
      status: changes.survey.currentValue.status,
      channelType: this.survey.channelType,
    });
  }

  private _handleSvpChildSelectionChanges(): void {
    const nonMcSvpSurvey$ = this._surveyCommand$.pipe(
      filter((s) => !s.mc?.SVP)
    );

    const mcSvpChildSurvey$ = this._selectedSvpChildSurveyIdCommand$.pipe(
      filter((childId) => !!childId),
      switchMap((childId) =>
        this._buyerApiService.getSurveyById(childId, true)
      ),
      map(
        (response) =>
          this._surveyPayloadService.mapBuyerSurvey(response) as Survey
      ),
      tap(
        (selectedSvpChildSurvey) =>
          (this.selectedSvpChildSurvey = selectedSvpChildSurvey)
      )
    );

    this._subscription.add(
      merge(nonMcSvpSurvey$, mcSvpChildSurvey$).subscribe((survey) => {
        this.dynamicTabSurvey = survey;
      })
    );

    this._subscription.add(
      this._surveyCommand$.subscribe((survey) => {
        this.staticTabSurvey = survey;
      })
    );
  }

  public trackByProperty(_: number, element: Tab): string {
    return element.key;
  }

  ngOnDestroy() {
    this._subscription.unsubscribe();
  }
}
interface Tab {
  key: string;
  title: string;
  active: boolean;
  className?: string;
  hasSpecialTheme?: boolean;
}
